import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Container,
  Row,
  Col,
  CardGroup,
  Card,
  CardBody,
  Form,
  FormFeedback,
  CustomInput,
  Input,
  Label,
} from 'reactstrap';
import { useMutation } from 'react-apollo';
import { FormattedMessage } from 'react-intl';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { CustomButton, CustomDashboardInput, ArrowLinkButton } from '../../../containers';
import AdminCreatePostMutation from '../../../operations/mutations/AdminCreatePostMutation';
import { validateFormSubmission } from '../../../utils/validation';
import { renderToaster, SUCCESS_TOASTER, ERROR_TOASTER } from '../../../constants/toaster';
import {
  handleRequestError,
  UPLOAD_FILE_EXCEEDS_MAX_SIZE,
  getSpecificError,
  UNKNOWN_PROFILE_ADVANCED_DATA_UPDATE,
} from '../../../constants/errorCodes';
import { ROUTE_TO_ADMIN_NEWS_PAGE } from '../../../constants/routes';
import GetCMSPostsForAdminQuery from '../../../operations/queries/GetCMSPostsForAdminQuery';
import { uploadFileToS3bucket, getFormData } from '../../../utils/imageUpload';
import { redirectToAdminPreviewPostPage } from '../../../redux/actions/navigationActions';
import { RequestUploadAccessMutation } from '../../../operations/mutations/RequestUploadAccessMutation';
import { UPLOAD_TYPE_FEATURED_IMAGE } from '../../../constants/uploadFileTypes';

export const AdminCreatePostPage = () => {
  const dispatch = useDispatch();
  const [title, setTitle] = useState({
    value: '',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.title" />,
    validation: {
      required: true,
    },
  });
  const [subtitle, setSubtitle] = useState({
    value: '',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.subtitle" />,
    validation: {
      required: true,
    },
  });
  const [published, setPublished] = useState({
    value: false,
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.published" />,
    validation: {
      required: true,
    },
  });
  const [content, setContent] = useState({
    value: '',
    error: null,
    invalid: false,
    focused: false,
    displayName: <FormattedMessage id="inputs.content" />,
    validation: {
      required: true,
    },
  });

  const [featuredImageUrl, setFeaturedImageUrl] = useState(null);
  const [displayImageUrl, setDisplayImageUrl] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const handleSuccess = data => {
    setSubmitting(true);
    dispatch(redirectToAdminPreviewPostPage(data.createPost.id));
    renderToaster('successMessages.postCreated', SUCCESS_TOASTER, { intlKey: true });
  };

  const [createPost, { loading }] = useMutation(AdminCreatePostMutation, {
    onError: error => handleRequestError(error, dispatch, { silent: false }, () => setSubmitting(false)),
    onCompleted: handleSuccess,
    refetchQueries: [{ query: GetCMSPostsForAdminQuery }],
  });

  const [requestUpload, { loading: requestUploadLoading }] = useMutation(RequestUploadAccessMutation, {
    onError: error => handleRequestError(error, dispatch, { silent: false }, () => setSubmitting(false)),
    onCompleted: async data => {
      const signedUrlsData = JSON.parse(data.requestUploadAccess.json);

      if (signedUrlsData.maxFileSize < featuredImageUrl.size) {
        renderToaster(getSpecificError(UPLOAD_FILE_EXCEEDS_MAX_SIZE).message, ERROR_TOASTER);
      } else {
        const formData = getFormData(featuredImageUrl, signedUrlsData);
        const fileUploadRes = await uploadFileToS3bucket(signedUrlsData.subdomainUrl, formData);

        if (fileUploadRes.status !== 204) {
          renderToaster(getSpecificError(UNKNOWN_PROFILE_ADVANCED_DATA_UPDATE).message, ERROR_TOASTER);
        } else {
          createPost({
            variables: {
              featuredImageUrl: signedUrlsData.resourceUrl,
              title: title.value,
              subtitle: subtitle.value,
              content: content.value,
              published: published.value,
            },
          });
        }
      }
    },
  });

  const handleCreatePost = async event => {
    event.preventDefault();
    setSubmitting(true);

    const fieldsArray = [setTitle, setSubtitle, setContent];

    if (await validateFormSubmission(fieldsArray)) {
      if (featuredImageUrl) {
        requestUpload({ variables: { uploadType: UPLOAD_TYPE_FEATURED_IMAGE } });
      } else {
        createPost({
          variables: {
            title: title.value,
            subtitle: subtitle.value,
            content: content.value,
            published: published.value,
          },
        });
      }
    } else {
      setSubmitting(false);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
      ['clean'],
    ],
  };

  const formats = ['header', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'indent', 'link'];

  const inputHandler = (setter, value) => {
    setter(o => ({ ...o, value, invalid: false }));
  };

  return (
    <Container className="mt-4" fluid>
      <ArrowLinkButton xlColSize="8" linkTo={ROUTE_TO_ADMIN_NEWS_PAGE} />
      <Row className="my-4">
        <Col xs="12" xl={{ size: 8, offset: 2 }}>
          <Row className="mb-3">
            <Col xs="12" className="">
              <div className="d-flex align-items-center">
                <h3 className="mb-0">
                  <FormattedMessage id="adminCreatePostPage.pageTitle" />
                </h3>
              </div>
            </Col>
          </Row>
          <Form onSubmit={handleCreatePost}>
            <Row>
              <Col>
                <CardGroup>
                  <Card>
                    <CardBody className="py-4">
                      <Row className="mb-2">
                        <Col>
                          <h3>
                            <FormattedMessage id="adminCreatePostPage.bannerImage" />
                          </h3>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Label className="m-0">
                            <div className="hok-upload-button btn btn-outline-primary">
                              <FormattedMessage id="buttons.setImage" />
                            </div>
                            <Input
                              type="file"
                              id="avatar"
                              name="avatar"
                              hidden
                              onChange={async event => {
                                if (event.target.files[0] && typeof event.target.files[0] !== 'undefined') {
                                  const reader = new FileReader();
                                  reader.onload = event => {
                                    setDisplayImageUrl(event.target.result);
                                  };
                                  reader.readAsDataURL(event.target.files[0]);
                                  setFeaturedImageUrl(event.target.files[0]);
                                }
                              }}
                            />
                          </Label>
                        </Col>
                      </Row>
                      {displayImageUrl && (
                        <Row className="mt-3">
                          <Col xs="12">
                            <img
                              className="img-fluid"
                              style={{ maxHeight: '250px', width: 'auto' }}
                              src={displayImageUrl}
                              alt={title || 'Featured image'}
                            />
                          </Col>
                        </Row>
                      )}
                      <Row className="mt-5 mb-2">
                        <Col>
                          <h3>
                            <FormattedMessage id="adminCreatePostPage.title" />
                          </h3>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs="12" sm="8">
                          <CustomDashboardInput
                            id="title"
                            fieldState={title}
                            onChange={event => inputHandler(setTitle, event.target.value)}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-5 mb-2">
                        <Col>
                          <h3>
                            <FormattedMessage id="adminCreatePostPage.subTitle" />
                          </h3>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs="12" sm="8">
                          <CustomDashboardInput
                            id="subtitle"
                            fieldState={subtitle}
                            onChange={event => inputHandler(setSubtitle, event.target.value)}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-5 mb-2">
                        <Col>
                          <h3>
                            <FormattedMessage id="adminEditPostPage.published" />
                          </h3>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs="12">
                          <CustomInput
                            inline
                            type="radio"
                            id="true"
                            checked={published.value}
                            name="published"
                            label={<FormattedMessage id="buttons.yes" />}
                            onChange={() => inputHandler(setPublished, true)}
                          />
                          <CustomInput
                            inline
                            type="radio"
                            id="false"
                            checked={!published.value}
                            name="published"
                            label={<FormattedMessage id="buttons.no" />}
                            onChange={() => inputHandler(setPublished, false)}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-5 mb-2">
                        <Col>
                          <h3>
                            <FormattedMessage id="adminCreatePostPage.mainText" />
                          </h3>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs="12">
                          <ReactQuill
                            className={'hok-text-editor' + (content.invalid ? ' is-invalid' : '')}
                            value={content.value}
                            onChange={value => inputHandler(setContent, value)}
                            modules={modules}
                            formats={formats}
                          />
                          {content.invalid && <FormFeedback className="d-block">{content.error}</FormFeedback>}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </CardGroup>
              </Col>
            </Row>
            <Row className="no-gutters mt-5">
              <Col className="text-right" xs="12">
                <CustomButton
                  type="submit"
                  className="hok-dashboard-btn d-block d-sm-inline-block ml-auto"
                  block={false}
                  translationId="create"
                  loaderProp={submitting || loading || requestUploadLoading}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};
